/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable block-scoped-var, no-restricted-syntax, prefer-destructuring */
import React, { useState, useMemo, useEffect } from 'react';
import { Form, Button, Select, SelectItem, TextInput, InlineNotification } from '@carbon/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import vkbeautify from 'vkbeautify';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import EndpointSelector from './EndpointSelector';
import TestToolSecurity from './TestToolSecurity';
// eslint-disable-next-line import/no-named-as-default
import TestToolParameters from './TestToolParameters';
import TestToolWebsocket from './TestToolWebsocket';
import TestToolResponse from './TestToolResponse';
import Invoker from '../utils/invoker';
import { objectToArray } from '../utils/common';
import { useApi } from '../contexts/ApiHandler';

function TestTool({ initialEndpoint, operation, operationPath, operationMethod, options, apps, onChangeEndpoint = () => {} }) {
  const { t } = useTranslation();
  const { api, apiHandler } = useApi();
  const invoker = useMemo(() => new Invoker(), []);
  const [schemes, setSchemes] = useState([]);
  const [scheme, setScheme] = useState(null);
  const [type, setType] = useState('');
  const [version, setVersion] = useState('');
  const [endpoint, setEndpoint] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [variables, setVariables] = useState([]);
  const [response, setResponse] = useState({});
  const [websocket, setWebsocket] = useState({});
  const [showWebsocketUI, setShowWebsocketUI] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [responseBody, setResponseBody] = useState(false);
  const [isInvoking, setIsInvoking] = useState(false);
  const [contentFormat, setContentFormat] = useState('html');
  const [parameterValues, setParameterValues] = useState({
    contentType: '',
    accept: '',
    parameterValues: {}
  });
  const [security, setSecurity] = useState({
    userCredentials: {
      username: null,
      password: null
    },
    httpDigestAuth: {
      digestUsername: null,
      digestRealm: '',
      digestNonce: '',
      digestOpaque: '',
      digestResponse: ''
    },
    httpHobaAuth: {
      hobaKeyIdentifier: '',
      hobaChallenge: '',
      hobaNonce: '',
      hobaSignature: ''
    },
    httpMutualAuth: {
      mutualVersion: '',
      mutualSid: '',
      mutualNonce: '',
      mutualVkc: ''
    },
    httpNegotiateAuth: {
      negotiateGssapiData: ''
    },
    httpOAuthAuth: {
      oauthRealm: '',
      oauthConsumerKey: '',
      oauthToken: '',
      oauthSignatureMethod: '',
      oauthSignature: '',
      oauthTimestamp: '',
      oauthNonce: '',
      oauthVersion: ''
    },
    httpVapidAuth: {
      vapidJSONWebToken: '',
      vapidJSONWebKey: ''
    },
    httpScramShaAuth: {
      scramSessionId: '',
      scramEncryptedCreds: ''
    },
    identification: {
      clientID: null,
      clientSecret: '',
      redirectUri: null,
      externalApiKeys: {}
    },
    flags: {},
    scopes: [],
    authToken: {
      code: '',
      access_token: ''
    }
  });

  useEffect(() => {
    setEndpoint(initialEndpoint);
  }, [initialEndpoint]);

  useEffect(() => {
    if (api) {
      const endpointOptions = { path: operationPath, method: operationMethod };
      let endpoints = apiHandler?.getEndpoints(api, endpointOptions);
      if (endpoints && endpoints.length === 0) {
        endpoints = ['https://undefined'];
      }
      setEndpoints(endpoints);
      setType(apiHandler?.getType(api));
      setVersion(apiHandler?.getVersion(api));
      let endpoint;
      if (typeof endpoint === 'undefined' && endpoints && endpoints.length > 0) {
        endpoint = endpoints[0].url;
        setEndpoint(endpoint);
        setVariables(endpoints[0].variables);
      }

      if (schemes && schemes.length === 0) {
        const schemes = apiHandler?.getSchemes(api);
        setSchemes(schemes);
        if (schemes) {
          setScheme(schemes[0]);
        }
      }
      let variables;
      if (endpoint) {
        variables = apiHandler?.getServerVariables(api, endpoint, { path: operationPath, method: operationMethod });
        setEndpoint(endpoint);
      }

      if (variables) {
        Object.values(variables).forEach((variable) => {
          if (!variable.value) {
            variable.value = variable.default;
          }
        });
      }
      setVariables(variables);

      const parameters = {};
      parameters.accept = apiHandler?.getDefaultAcceptHeader(operation, api);
      parameters.contentType = apiHandler?.getDefaultContentTypeHeader(operation, api);

      const incomingParams = apiHandler?.getParameters(operation, operationPath, parameters.contentType, api);

      if (
        operation.requestBody &&
        0 in Object.keys(operation.requestBody.content) &&
        operation.requestBody.content[Object.keys(operation.requestBody.content)[0]].encoding &&
        (Object.keys(operation.requestBody.content)[0] === 'application/x-www-form-urlencoded' ||
          Object.keys(operation.requestBody.content)[0].startsWith('multipart/'))
      ) {
        // eslint-disable-next-line no-var, vars-on-top
        var encodingObject = operation.requestBody.content[Object.keys(operation.requestBody.content)[0]].encoding;
      }
      if (incomingParams) {
        // eslint-disable-next-line guard-for-in
        for (const key in incomingParams) {
          if (encodingObject && (incomingParams[key].in === 'formData' || incomingParams[key].in === 'body') && encodingObject[key]) {
            incomingParams[key].encoding = encodingObject[key];
          }

          // ( parameter.enum && parameter.required )
          if (incomingParams[key].enum && incomingParams[key].required) {
            incomingParams[key].value = incomingParams[key].enum[0];
          } else if (incomingParams[key].type === 'boolean' && incomingParams[key].required) {
            incomingParams[key].value = false;
          } else {
            incomingParams[key].value = undefined;
          }
        }
        parameters.parameterValues = incomingParams;
      } else {
        parameters.parameterValues = {};
      }

      if (
        options.showMTLSHeader &&
        api['x-ibm-configuration'] &&
        api['x-ibm-configuration']['application-authentication'] &&
        api['x-ibm-configuration']['application-authentication'].certificate === true
      ) {
        const clientCertificateHeader = {};
        clientCertificateHeader.name = 'X-Client-Certificate';
        clientCertificateHeader.in = 'header';
        clientCertificateHeader.required = true;
        clientCertificateHeader.type = 'string';
        clientCertificateHeader.description = 'x_client_certficate_header';
        clientCertificateHeader.value = '';

        parameters.parameterValues['X-Client-Certificate'] = clientCertificateHeader;
      }
      setParameterValues(parameters);
      setShowRequest(false);
    }
  }, [api, apiHandler, operation, operationMethod, operationPath, options.showMTLSHeader, schemes]);

  const handleEndpointChange = (event) => {
    const basePlusOpPath = apiHandler?.getOperationPath(api, operationPath);
    const endpoint = event.target.value;
    const variables = apiHandler?.getServerVariables(api, endpoint, { path: basePlusOpPath, method: operationMethod });

    if (websocket && (websocket.readyState === 0 || websocket.readyState === 1)) {
      websocket.close();
    }
    if (variables) {
      Object.values(variables).forEach((variable) => {
        variable.value = variable.default;
      });
    }
    setEndpoint(endpoint);
    setVariables(variables);
    onChangeEndpoint(event);
  };
  const handleVariableChange = (update) => {
    if (update.target && update.target.name && update.target.value !== undefined) {
      const newVariables = { ...variables };
      newVariables[update.target.name].value = update.target.value;
      setVariables(newVariables);
    }
  };
  const handleSecurityChange = (update) => {
    const newSecurity = Object.assign(security, update);
    setSecurity(newSecurity);
  };
  const handleParameterChange = (update) => {
    const newParameterValues = { ...parameterValues, ...update };
    setParameterValues(newParameterValues);
  };
  // Function to check if a value is an empty array
  const isEmptyArray = (value) =>
    // Returns true if the value is an array and has a length of 0, otherwise false
    Array.isArray(value) && value.length === 0;
  // Function to validate the schema object
  const isSchemaValid = (schema) =>
    // If schema is not defined, return true
    !schema ||
    // Ensure schema type is not 'boolean'
    (schema.type !== 'boolean' &&
      // If schema.required is not defined OR it is a non-empty array, return true
      (!schema.required || (Array.isArray(schema.required) && schema.required.length > 0)));
  const checkForMissingValues = (operationParameters) => {
    // check the parameters. look for any that are required but have no value.
    let missingRequiredParams = false;

    objectToArray(operationParameters).map((parameterKeyValue) => {
      // Need to check in parameters.parameterValues for a match if this param is required
      if (
        parameterKeyValue.value.required && // Check if 'required' property in parameterKeyValue.value is true
        (!parameterValues.parameterValues || // Check if parameterValues.parameterValues is undefined or null
          !parameterValues.parameterValues[parameterKeyValue.key] || // Check if the specific key is undefined or null
          // Check if the value is undefined or empty array
          ((!parameterValues.parameterValues[parameterKeyValue.key].value || isEmptyArray(parameterValues.parameterValues[parameterKeyValue.key].value)) &&
            // Ensure the type is not 'boolean'
            parameterValues.parameterValues[parameterKeyValue.key].type !== 'boolean' &&
            // Validate the schema using the isSchemaValid function
            isSchemaValid(parameterValues.parameterValues[parameterKeyValue.key].schema)))
      ) {
        missingRequiredParams = true;
      }
      return null;
    });

    return missingRequiredParams;
  };
  const makeInvokeConfigObject = () => {
    const basePlusOpPath = apiHandler?.getOperationPath(api, operationPath);
    const isSoap = apiHandler?.isSOAP(api);

    let invokeEndpoint = endpoint;
    if (!/(wss?|https?):\/\//.test(endpoint)) {
      if (scheme) {
        invokeEndpoint = `${scheme}://${endpoint}`;
      } else {
        invokeEndpoint = `https://${endpoint}`;
      }
    }

    const invokeConfig = {
      verb: operationMethod,
      operationPath: basePlusOpPath,
      endpoint: invokeEndpoint,
      variables,
      type,
      version,
      scheme,
      contentTypeHeader: parameterValues.contentType,
      acceptHeader: parameterValues.accept,
      username: security.userCredentials.username,
      password: security.userCredentials.password,
      clientId: security.identification.clientID,
      clientSecret: security.identification.clientSecret,
      parameterValues: parameterValues.parameterValues,
      apiKeys: security.identification.externalApiKeys,
      authToken: security.authToken,
      httpBearerToken: security.httpBearerToken,
      httpDigestAuth: security.httpDigestAuth,
      httpHobaAuth: security.httpHobaAuth,
      httpMutualAuth: security.httpMutualAuth,
      httpNegotiateAuth: security.httpNegotiateAuth,
      httpOAuthAuth: security.httpOAuthAuth,
      httpVapidAuth: security.httpVapidAuth,
      httpScramShaAuth: security.httpScramShaAuth,
      proxyUrl: null
    };

    if (options && options.proxyUrl) {
      invokeConfig.proxyUrl = options.proxyUrl;
    }

    // deal with certificateStripPrefix &
    // certificateStripNewlines
    if (invokeConfig.parameterValues) {
      if (invokeConfig.parameterValues['X-Client-Certificate']) {
        let certificate = invokeConfig.parameterValues['X-Client-Certificate'].value;

        if (certificate != null && options && options.certificateStripPrefix) {
          certificate = certificate.replace(/-----BEGIN CERTIFICATE-----|-----END CERTIFICATE-----/, '');
        }

        if (certificate != null && options && options.certificateStripNewlines) {
          certificate = certificate.replace(/\s+/g, '');
          certificate = certificate.replace('BEGINCERTIFICATE', 'BEGIN CERTIFICATE');
          certificate = certificate.replace('ENDCERTIFICATE', 'END CERTIFICATE');
        }

        invokeConfig.parameterValues['X-Client-Certificate'].value = certificate;
      }

      for (const key in invokeConfig.parameterValues) {
        if (invokeConfig.parameterValues[key].schema && invokeConfig.parameterValues[key].schema.$ref) {
          invokeConfig.parameterValues[key].schema = apiHandler?.getLocalReference(invokeConfig.parameterValues[key].schema.$ref, api);
        }
      }
    }

    if (isSoap && operation['x-ibm-soap'] && operation['x-ibm-soap']['soap-action']) {
      invokeConfig.soapAction = operation['x-ibm-soap']['soap-action'];
    }

    return invokeConfig;
  };
  const connectWebsocket = () => {
    const invokeConfig = makeInvokeConfigObject();

    const websocket = invoker.invokeWebsocket(invokeConfig, security.flags);

    setWebsocket(websocket);
    setShowWebsocketUI(true);
    setShowRequest(false);
  };
  const handleInvokeResponse = async (response) => {
    if (response.responseDataString && response.statusCode * 1 !== 0) {
      let contentFormat = 'html';
      let responseString = response.responseDataString;
      if (response && response.headers && response.headers['content-type']) {
        if (response.headers['content-type'].indexOf('text/plain') === 0) {
          contentFormat = 'markdown';
        } else if (response.headers['content-type'].indexOf('application/json') === 0) {
          contentFormat = 'json';
          try {
            responseString = vkbeautify.json(response.responseDataString);
          } catch {
            contentFormat = 'markdown';
          }
        } else if (response.headers['content-type'].indexOf('application/xml') === 0) {
          contentFormat = 'xml';
          try {
            responseString = vkbeautify.xml(response.responseDataString);
          } catch {
            contentFormat = 'markdown';
          }
        } else if (response.isFileDownload) {
          let fileName = '';
          const filenameMatch = response.headers['content-disposition']?.match(/filename\*?="?(.+)"?/);
          if (filenameMatch && filenameMatch.length > 1) {
            fileName = filenameMatch[1];
          } else if (response.request.url || operationPath) {
            fileName = response.request?.url ?? operationPath;
            const parts = response.request.url.split('/');
            fileName = parts[parts.length - 1];
          }

          const url = URL.createObjectURL(response.responseDataString);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
        if (!responseString) {
          responseString = response.responseDataString;
        }
      }

      const responseValue = responseString;

      setResponseBody(responseValue);
      setContentFormat(contentFormat);
    }

    setResponse(response);
    setIsInvoking(false);
  };
  const handleTestRequest = async () => {
    setIsInvoking(true);
    setResponse({});
    setShowRequest(true);
    setShowWebsocketUI(false);

    const invokeConfig = makeInvokeConfigObject();

    try {
      const response = await invoker.invoke(invokeConfig, security.flags);
      handleInvokeResponse(response);
    } catch (err) {
      handleInvokeResponse(err);
    }
  };
  const handleTestReset = () => {
    const newVariables = variables;
    if (newVariables) {
      Object.values(newVariables).forEach((variable) => {
        variable.value = variable.default;
      });
      setVariables(newVariables);
    }
    if (websocket && (websocket.readyState === 0 || websocket.readyState === 1)) {
      websocket.close();
    }
    setWebsocket(null);
    setShowWebsocketUI(false);
    const parameters = {};
    parameters.accept = apiHandler?.getDefaultAcceptHeader(operation, api);
    parameters.contentType = apiHandler?.getDefaultContentTypeHeader(operation, api);

    let incomingParams = JSON.parse(JSON.stringify(apiHandler?.getParameters(operation, operationPath, parameters.contentType, api)));
    incomingParams = apiHandler?.removeReservedParameters(incomingParams);

    if (incomingParams) {
      for (const key in incomingParams) {
        if (incomingParams[key].enum && incomingParams[key].required) {
          incomingParams[key].value = incomingParams[key].enum[0];
        } else if (incomingParams[key].type === 'boolean' && incomingParams[key].required) {
          incomingParams[key].value = false;
        } else {
          incomingParams[key].value = undefined;
        }
      }

      parameters.parameterValues = incomingParams;
    } else {
      parameters.parameterValues = {};
    }
    setParameterValues(parameters);
    setShowRequest(false);
  };

  const contentType = parameterValues.contentType ?? apiHandler?.getDefaultContentTypeHeader(operation, api);
  let parameters = apiHandler?.getParameters(operation, operationPath, contentType, api);
  parameters = apiHandler?.removeReservedParameters(parameters);
  const isSoap = apiHandler?.isSOAP(api);
  const securityConfig = apiHandler?.getSecurityConfig(api, operation, null, endpoint);
  const isEnforced = apiHandler?.isEnforced();
  const missingRequiredParams = parameters ? checkForMissingValues(parameters) : true;
  const basePlusOpPath = apiHandler?.getOperationPath(api, operationPath);
  const isWebsocket = /(wss?):\/\//.test(endpoint);
  const accepts = !isWebsocket ? apiHandler?.getAccepts(operation, api) : null;
  const contentTypes = !isWebsocket ? apiHandler?.getContentTypes(operation, api) : null;
  const insecure = typeof endpoint === 'string' && (endpoint?.startsWith('http://') || endpoint?.startsWith('ws://'));
  const mtlsEnabled = get(api, "['x-ibm-configuration']['application-authentication']['certificate']") === true;
  const bodyParam =
    operation.requestBody &&
    (contentType.startsWith('multipart/') || contentType === 'application/x-www-form-urlencoded') &&
    apiHandler?.getFormDataParameters(operation, operationPath, contentType, api).length > 0
      ? null
      : apiHandler?.getBodyParameter(operation, operationPath, contentType, api);

  // For the case where an API requires invokers to provide their application certificate
  // we need to add another header (supports mutual TLS per https://www.ibm.com/support/knowledgecenter/en/SSMNED_5.0.0/com.ibm.apic.toolkit.doc/task_apionprem_creating_apis.html#task_tq2_11r_xt__tls_mutual_auth)
  if (options.showMTLSHeader && mtlsEnabled && parameters) {
    const clientCertificateHeader = {};
    clientCertificateHeader.name = 'X-Client-Certificate';
    clientCertificateHeader.in = 'header';
    clientCertificateHeader.required = true;
    clientCertificateHeader.type = 'string';
    clientCertificateHeader.description = t('x_client_certficate_header');
    parameters['X-Client-Certificate'] = clientCertificateHeader;
  }

  return (
    <section className={classNames('tab-content', 'testTool')} aria-label="test-tool">
      {securityConfig && securityConfig.securityFlows && !isEnforced && (
        <InlineNotification kind="info" className="testTool__information" title={t('request_security_credentials')} hideCloseButton lowContrast />
      )}
      {insecure && window.location.protocol === 'https:' && (
        <InlineNotification
          kind="warning"
          className="testTool__information"
          title={t('APIs with insecure protocols cannot be invoked in a secure context.')}
          hideCloseButton
          lowContrast
        />
      )}
      <Form className="testForm">
        <div className="testToolSection testToolEndpoints">
          <div className={classNames('labelText', { method: !isSoap }, operationMethod)}>
            {!isSoap && operationMethod}
            {isSoap && t('endpoint')}
          </div>
          <div className="valueText">
            <EndpointSelector id="testtool-endpoint-select" endpoints={endpoints} opPath={basePlusOpPath} onChange={handleEndpointChange} endpoint={endpoint} />
          </div>
        </div>
        {variables && (
          <div className="testToolSection">
            <div className="labelText">{t('server_variables')}</div>
            <div>
              {Object.entries(variables).map((entry) => {
                const variableName = entry[0];
                const variable = entry[1];
                if (variable.enum) {
                  return (
                    <div key={variableName} className={classNames('parameterOuter', `server-variable-${variableName}`)}>
                      <div className="serverVariableName">{variableName}</div>
                      <Select
                        data-testid={`server-variable-${variableName}`}
                        id={variableName}
                        hideLabel
                        className="cds--select__input"
                        required
                        name={variableName}
                        labelText={variableName}
                        value={variable.value}
                        onChange={handleVariableChange}
                      >
                        {variable.enum.map((value) => (
                          <SelectItem key={value} value={value} text={String(value)} />
                        ))}
                      </Select>
                    </div>
                  );
                }
                return (
                  <div key={variableName} className={classNames('parameterOuter', `server-variable-${variableName}`)}>
                    <div className="serverVariableName">{variableName}</div>
                    <TextInput
                      data-testid={`server-variable-${variableName}`}
                      id={variableName}
                      className="variable-input"
                      onChange={handleVariableChange}
                      labelText={variableName}
                      hideLabel
                      required
                      value={variable.value}
                      name={variableName}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {securityConfig && securityConfig.securityFlows && (
          <TestToolSecurity
            config={securityConfig}
            options={options}
            apps={apps}
            onChange={handleSecurityChange}
            t={t}
            endpoint={endpoint}
            isEnforced={isEnforced}
          />
        )}
        {((parameters && Object.keys(parameters).length > 0) || (accepts && accepts.length > 0) || (contentTypes && contentTypes.length > 0)) && (
          <TestToolParameters
            parameters={parameters}
            accepts={accepts}
            contentTypes={contentTypes}
            body={bodyParam}
            api={api}
            operationPath={operationPath}
            operationMethod={operationMethod}
            t={t}
            onChange={handleParameterChange}
            parameterState={parameterValues}
            options={options}
          />
        )}

        <div className="testToolFormButtons">
          <Button kind="secondary" className="testToolReset" onClick={handleTestReset}>
            {t('reset')}
          </Button>
          {!isWebsocket && (
            <Button className="testToolSend" disabled={isInvoking || missingRequiredParams} onClick={handleTestRequest}>
              {t('send')}
            </Button>
          )}

          {isWebsocket && (!websocket || websocket.readyState !== 1) && (
            <Button className="testToolConnect" disabled={(websocket && websocket.readyState === 0) || missingRequiredParams} onClick={connectWebsocket}>
              {t('connect')}
            </Button>
          )}
          {isWebsocket && websocket && websocket.readyState === 1 && (
            <Button
              className="testToolDisconnect"
              onClick={() => {
                websocket.close();
              }}
            >
              {t('disconnect')}
            </Button>
          )}
        </div>
      </Form>
      {showWebsocketUI && <TestToolWebsocket websocket={websocket} />}
      {showRequest && (
        <TestToolResponse
          security={security}
          invokeResponse={response}
          isInvoking={isInvoking}
          responseBody={responseBody}
          contentFormat={contentFormat}
          mtlsEnabled={mtlsEnabled}
          t={t}
        />
      )}
    </section>
  );
}

TestTool.propTypes = {
  api: PropTypes.object,
  operation: PropTypes.object,
  operationPath: PropTypes.string,
  operationMethod: PropTypes.string,
  options: PropTypes.object,
  apps: PropTypes.array,
  onChangeEndpoint: PropTypes.func
};

export default TestTool;

// WEBPACK FOOTER //
// src/components/TestTool.js
